import angular from 'angular';
import TokenService from '../services/token';
import Config from '../services/config';

/**
 * AngularJS <-> React integration layer.
 *
 * The modern React code is built separately (see `frontend/react`) into a
 * global `window.ReactBridge`. This module lets the legacy AngularJS shell:
 *
 *   1. Mount a React view into any element:
 *        <div react-view="hello" props="vm.someData"></div>
 *      and cleanly unmount it when the Angular scope is destroyed.
 *
 *   2. Keep AngularJS in sync with the shared vanilla Zustand store, so state
 *      changes made in React trigger an Angular digest.
 *
 *   3. Give the shared REST resource stores (`ReactBridge().getResourceStore`)
 *      access to the auth token + 401 handling, so they can talk to the API
 *      exactly like the legacy Restangular-based stores did.
 */

const MODULE = 'app.react';

angular
  .module(MODULE, [TokenService.$angularModule.name, Config.$angularModule.name])
  .config(function ($provide) {
    'ngInject';
    $provide.decorator('$exceptionHandler', function ($delegate) {
      'ngInject';
      return function (exception, cause) {
        if (window.ReactBridge) {
          window.ReactBridge.captureException(exception);
        }
        $delegate(exception, cause);
      };
    });
  })

  // Thin accessor so the rest of the Angular app can inject the bridge/store
  // instead of reaching for the window global directly.
  .factory('ReactBridge', function () {
    'ngInject';
    return function getBridge() {
      if (!window.ReactBridge) {
        throw new Error(
          '[app.react] window.ReactBridge is not loaded. ' +
            'Ensure /react-bridge/bridge.js is included in index.html and built ' +
            '(cd frontend/react && yarn build).',
        );
      }
      return window.ReactBridge;
    };
  })

  /**
   * Wrapper directive that mounts a React route into its host element.
   *
   * Usage:
   *   <div react-view="hello"></div>
   *   <div react-view="hello" props="vm.data"></div>
   */
  .directive('reactView', function (ReactBridge) {
    'ngInject';
    return {
      restrict: 'A',
      scope: {
        route: '@reactView',
        props: '<?',
      },
      link: function (scope, element) {
        const host = element[0];

        function render() {
          ReactBridge().mount(host, scope.route, scope.props || {});
        }

        render();

        // Re-render React when the passed-in props change.
        scope.$watch('props', render, true);

        // React 18 needs an explicit unmount to avoid leaking the tree.
        scope.$on('$destroy', function () {
          if (window.ReactBridge) {
            window.ReactBridge.unmount(host);
          }
        });
      },
    };
  })

  /**
   * Connector: when the shared store changes (e.g. from a React component),
   * schedule an Angular digest so any Angular bindings reflecting that state
   * update. `$applyAsync` coalesces bursts of updates into a single digest.
   */
  .run(function ($rootScope) {
    'ngInject';
    if (window.ReactBridge && window.ReactBridge.store) {
      window.ReactBridge.store.subscribe(function () {
        $rootScope.$applyAsync();
      });
    }
  })

  /**
   * Give the shared REST resource stores (`ReactBridge().getResourceStore`)
   * the same auth token + 401 handling the legacy Restangular `R` factory
   * uses (see `app/resource.js`), so they can talk to the API identically.
   */
  .run(function ($state, TokenService, Config) {
    'ngInject';
    if (window.ReactBridge) {
      window.ReactBridge.configureResourceAuth({
        getToken: () => TokenService.get(),
        isApiV2Enabled: () => Config.isFeatureFlagActive('ENABLE_API_V2', false),
        onUnauthorized: () => {
          TokenService.clear();
          $state.go('r.login', {force: true});
        },
      });
    }
  })

  /**
   * Seamless SPA navigation handler for React components.
   * When React calls `window.ReactBridge.navigate(url)`, update Angular's
   * $location and trigger a digest so UI-Router transitions cleanly without page reload.
   */
  .run(function ($location, $rootScope, $state) {
    'ngInject';
    if (window.ReactBridge) {
      window.ReactBridge.replaceUrl = function (url) {
        $rootScope.$evalAsync(function () {
          const params = angular.copy($state.params);
          Object.keys($location.search()).forEach(function (key) {
            delete params[key];
          });
          $location.url(url);
          angular.extend(params, $location.search());
          $state.go($state.current.name, params, {
            notify: false,
            location: 'replace',
            inherit: false,
          });
        });
      };
      window.ReactBridge.navigate = function (url) {
        $rootScope.$applyAsync(function () {
          $location.url(url);
        });
      };
    }
  });

export default MODULE;



// WEBPACK FOOTER //
// ./src/app/react/react.js